import clsx from 'clsx';
import { useRef, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useWindowWidth } from '@react-hook/window-size';

import { PhotoAlbumFieldsFragment, PhotoFieldsFragment } from 'api/graphql-types';
import Image from 'components/image';
import { Button } from 'components/button';
import { Typography } from 'components/typography';
import { Icon } from 'components/icon/icon';

import IconChevronRight from 'public/assets/icons/chevron-right.svg';
import IconChevronLeft from 'public/assets/icons/chevron-left.svg';

import Modal from './modal';
import useScrollNavigation from './use-scroll-navigation';
import styles from './photo-album.module.scss';
import { sanitizeAndParseHtml } from 'utils/rich-text-renderer/sanitize-and-parse';

const PAGE_SIZE = 12;

export interface IPhotoAlbumProps {
	module: PhotoAlbumFieldsFragment;
}

export const PhotoAlbum = ({ module }: IPhotoAlbumProps): React.JSX.Element => {
	const { horizontal = false, id, jsonTitle, description, photos } = module;
	const { formatMessage } = useIntl();
	const trackRef = useRef<HTMLDivElement>(null);
	const windowWidth = useWindowWidth();

	const { hasNavigation, currentPage, nextEnabled, previousEnabled, onNextClick, onPreviousClick } =
		useScrollNavigation({
			trackRef,
			enabled: horizontal,
			itemCount: photos.length,
			pageSize: windowWidth < 1200 ? (windowWidth < 768 ? 1 : 2) : 3
		});

	// TODO: Fix Strapi pagination
	const paginatedPhotos: PhotoFieldsFragment[][] = useMemo(() => {
		const pages = [];

		if (horizontal) {
			pages.push(photos);
			return pages;
		}

		for (let index = 0; index < photos.length; index += PAGE_SIZE) {
			const page = photos.slice(index, index + PAGE_SIZE);
			pages.push(page);
		}

		return pages;
	}, [photos.length]);

	const [photoList, setPhotoList] = useState<PhotoFieldsFragment[]>(paginatedPhotos[0]);
	const [pageIndex, setPageIndex] = useState<number>(0);
	const [showModal, setShowModal] = useState<boolean>(false);
	const [activeItemIndex, setActiveItemIndex] = useState<number | undefined>();

	if (!module.photos || module.photos.length < 1) return <></>;

	const pageIndicatorId = `photo-album-${id}__page`;
	const transformOffsetStyle = { '--current-page': `-${currentPage || 0}` } as React.CSSProperties;
	const hasMorePhotos = paginatedPhotos.length - 1 > pageIndex;

	const onOpen = (activeIndex: number): void => {
		setActiveItemIndex(activeIndex);
		setShowModal(true);
	};

	const onClose = (): void => {
		setShowModal(false);
	};

	const onLoadMore = (): void => {
		if (hasMorePhotos) {
			setPhotoList((previousValue) => [...previousValue, ...paginatedPhotos[pageIndex + 1]]);
			setPageIndex((previousValue) => previousValue + 1);
		}
	};

	return (
		<div className={styles.wrapper}>
			{(jsonTitle.value || description) && (
				<div className={styles.header}>
					{jsonTitle.value && <Typography variant={jsonTitle.type || 'h4'}>{jsonTitle.value}</Typography>}
					{description && <>{sanitizeAndParseHtml(description)}</>}
				</div>
			)}

			{hasNavigation && (
				<div className={styles.navigation}>
					<button
						onClick={onPreviousClick}
						disabled={!previousEnabled}
						aria-controls={pageIndicatorId}
						aria-label={formatMessage({ id: 'modules.photoAlbum.previous.aria' }, { page: currentPage })}
					>
						<Icon customIcon={<IconChevronLeft />} size="16px" />
						{formatMessage({ id: 'modules.photoAlbum.previous' })}
					</button>
					<button
						onClick={onNextClick}
						disabled={!nextEnabled}
						aria-controls={pageIndicatorId}
						aria-label={formatMessage({ id: 'modules.photoAlbum.next.aria' }, { page: (currentPage || 1) + 1 })}
					>
						{formatMessage({ id: 'modules.photoAlbum.next' })}
						<Icon customIcon={<IconChevronRight />} size="16px" />
					</button>
				</div>
			)}

			<div
				ref={trackRef}
				className={clsx(styles.photoGrid, horizontal ? styles.horizontal : styles.vertical)}
				style={transformOffsetStyle}
				aria-live="polite"
			>
				<div
					id={pageIndicatorId}
					className={styles.pageIndicator}
					aria-live="polite"
					aria-label={formatMessage({ id: 'modules.photoAlbum.page' }, { page: (currentPage || 0) + 1 })}
				/>

				{photoList.map((item, index) => (
					<button
						key={`photo-item__${item.id}`}
						className={styles.photoItem}
						onClick={() => onOpen(index)}
						data-label={formatMessage({ id: 'modules.photoAlbum.open' })}
						aria-label={
							item.title || item.photo.data?.attributes?.alternativeText
								? formatMessage(
										{ id: 'modules.photoAlbum.open.named' },
										{ name: item.title || item.photo.data?.attributes?.alternativeText }
								  )
								: formatMessage({ id: 'modules.photoAlbum.open' })
						}
					>
						<Image
							attributes={item.photo?.data?.attributes}
							sizes="(max-width: 768px) 720px, (max-width: 1200px) 570px, 390px"
							imageSize="small"
							priority={index < 3}
							style={{ objectFit: 'cover' }}
							fill
						/>
					</button>
				))}
			</div>

			{!horizontal && hasMorePhotos && (
				<div className={styles.footer}>
					<Button variant="primary" onClick={onLoadMore} title={formatMessage({ id: 'modules.photoAlbum.more' })} />
				</div>
			)}

			{showModal && <Modal images={photoList} activeItem={activeItemIndex} onClose={onClose} />}
		</div>
	);
};

export default PhotoAlbum;
